import React, { useState, useEffect, createContext, useContext, useCallback, ReactNode } from 'react';

export interface Toast {
    id: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
    action?: {
        label: string;
        onClick: () => void;
    };
}

interface ToastContextType {
    toasts: Toast[];
    showToast: (message: string, type?: Toast['type'], duration?: number, action?: Toast['action']) => string;
    closeToast: (id: string) => void;
    success: (message: string, duration?: number, action?: Toast['action']) => string;
    error: (message: string, duration?: number, action?: Toast['action']) => string;
    warning: (message: string, duration?: number, action?: Toast['action']) => string;
    info: (message: string, duration?: number, action?: Toast['action']) => string;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<Toast[]>([]);

    const closeToast = useCallback((id: string) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const showToast = useCallback((
        message: string,
        type: Toast['type'] = 'info',
        duration: number = 5000,
        action?: Toast['action']
    ) => {
        const id = `toast-${Date.now()}-${Math.random()}`;
        const newToast: Toast = { id, message, type, duration, action };
        setToasts(prev => [...prev, newToast]);
        return id;
    }, []);

    const success = useCallback((message: string, duration?: number, action?: Toast['action']) =>
        showToast(message, 'success', duration, action), [showToast]);

    const error = useCallback((message: string, duration?: number, action?: Toast['action']) =>
        showToast(message, 'error', duration || 7000, action), [showToast]);

    const warning = useCallback((message: string, duration?: number, action?: Toast['action']) =>
        showToast(message, 'warning', duration, action), [showToast]);

    const info = useCallback((message: string, duration?: number, action?: Toast['action']) =>
        showToast(message, 'info', duration, action), [showToast]);

    // Expose to window for legacy/non-react access
    useEffect(() => {
        (window as any).TOAST = { success, error, warning, info };
        return () => { delete (window as any).TOAST; };
    }, [success, error, warning, info]);

    return (
        <ToastContext.Provider value={{ toasts, showToast, closeToast, success, error, warning, info }}>
            {children}
            <ToastContainer toasts={toasts} onClose={closeToast} />
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

interface ToastNotificationProps {
    toast: Toast;
    onClose: (id: string) => void;
}

const ToastNotification: React.FC<ToastNotificationProps> = ({ toast, onClose }) => {
    const [isExit, setIsExit] = useState(false);

    const handleClose = useCallback(() => {
        setIsExit(true);
        setTimeout(() => onClose(toast.id), 300);
    }, [onClose, toast.id]);

    useEffect(() => {
        if (toast.duration && toast.duration > 0) {
            const timer = setTimeout(handleClose, toast.duration);
            return () => clearTimeout(timer);
        }
    }, [toast.duration, handleClose]);

    const typeConfig = {
        success: {
            bg: 'bg-white',
            border: 'border-emerald-100',
            iconBg: 'bg-emerald-50',
            iconColor: 'text-emerald-500',
            barBg: 'bg-emerald-500',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                </svg>
            )
        },
        error: {
            bg: 'bg-white',
            border: 'border-rose-100',
            iconBg: 'bg-rose-50',
            iconColor: 'text-rose-500',
            barBg: 'bg-rose-500',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                </svg>
            )
        },
        warning: {
            bg: 'bg-white',
            border: 'border-amber-100',
            iconBg: 'bg-amber-50',
            iconColor: 'text-amber-500',
            barBg: 'bg-amber-500',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
            )
        },
        info: {
            bg: 'bg-white',
            border: 'border-indigo-100',
            iconBg: 'bg-indigo-50',
            iconColor: 'text-indigo-500',
            barBg: 'bg-indigo-500',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            )
        }
    };

    const config = typeConfig[toast.type];

    return (
        <div 
            className={`
                relative flex items-center gap-4 w-full max-w-[24rem] p-4 pr-12
                ${config.bg} ${config.border} border rounded-2xl shadow-2xl shadow-slate-200/50
                transition-all duration-300 pointer-events-auto
                ${isExit ? 'opacity-0 translate-x-12 scale-95' : 'animate-in slide-in-from-right-12 fade-in'}
            `}
        >
            {/* Left accent bar */}
            <div className={`absolute left-0 top-4 bottom-4 w-1 ${config.barBg} rounded-r-full`}></div>

            {/* Icon */}
            <div className={`flex items-center justify-center shrink-0 w-10 h-10 ${config.iconBg} ${config.iconColor} rounded-xl`}>
                {config.icon}
            </div>

            {/* Content */}
            <div className="flex-1">
                <p className="text-sm font-bold text-slate-800 leading-tight">
                    {toast.message}
                </p>
                {toast.action && (
                    <button 
                        onClick={() => { toast.action?.onClick(); handleClose(); }}
                        className={`mt-2 text-[10px] font-black uppercase tracking-widest ${config.iconColor} hover:underline`}
                    >
                        {toast.action.label}
                    </button>
                )}
            </div>

            {/* Close Button */}
            <button 
                onClick={handleClose}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-slate-300 hover:text-slate-500 hover:bg-slate-50 rounded-lg transition-colors"
            >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    );
};

const ToastContainer: React.FC<{ toasts: Toast[], onClose: (id: string) => void }> = ({ toasts, onClose }) => {
    return (
        <div className="fixed top-6 right-6 z-[9999] flex flex-col gap-4 pointer-events-none overflow-hidden p-4">
            {toasts.map(toast => (
                <ToastNotification key={toast.id} toast={toast} onClose={onClose} />
            ))}
        </div>
    );
};

